import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, take } from 'rxjs';

import { appActions, authActions } from '@libs/store';
import { CollaborationListenerService } from '../../services';
import { CollaborationScreenRecordingService } from '../../components';
import { collaborationsActions } from './collaborations.actions';

@Injectable({ providedIn: 'root' })
export class CollaborationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly collaborationListenerService = inject(CollaborationListenerService);
  private readonly collaborationScreenRecordingService = inject(CollaborationScreenRecordingService);

  public connect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appActions.appInitialized, authActions.authenticated),
        map(() => {
          this.collaborationListenerService.connect();
        }),
      );
    },
    { dispatch: false },
  );

  public disconnect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(authActions.onLogout),
        map(() => {
          this.collaborationListenerService.disconnect();
        }),
      );
    },
    { dispatch: false },
  );

  public screenRecordingRequested$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(collaborationsActions.screenRecordingRequested),
      filter(
        ({ collaborationId, siteId }) =>
          this.collaborationScreenRecordingService.recordingStatus() === 'stopped' && !!collaborationId && !!siteId,
      ),
      switchMap(({ collaborationId, siteId, patientId }) => {
        return this.collaborationScreenRecordingService.startRecording(collaborationId, siteId, patientId).pipe(take(1));
      }),
      filter((blob): blob is Blob => !!blob),
      map((blob) => {
        const now = new Date();
        const file = new File([blob], `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}-recording`, { type: blob.type });

        return collaborationsActions.screenRecordingSucceeded({ file });
      }),
    );
  });

  public screenRecordingStopRequested$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(collaborationsActions.screenRecordingStopRequested),
        map(() => {
          this.collaborationScreenRecordingService.stopRecording();
        }),
      );
    },
    { dispatch: false },
  );
}
